<script setup lang="ts">
import { ref } from 'vue'
import { hall } from '@/hall'

const emit = defineEmits(['navigate'])

const userId = ref(hall.userModule.userId || '500497')
const userName = ref(`Guest${userId.value.toString().slice(-4)}`)

const menuGroup1 = [
  { id: 'wallets', labelKey: 'AccountPanel_wallets', icon: 'wallet' },
  { id: 'vip', labelKey: 'AccountPanel_vip', icon: 'diamond' },
  { id: 'records', labelKey: 'AccountPanel_records', icon: 'document' },
  { id: 'rollover', labelKey: 'AccountPanel_rollover', icon: 'refresh' },
]

const menuGroup2 = [
  { id: 'mail', labelKey: 'AccountPanel_mail', icon: 'mail' },
  { id: 'support', labelKey: 'AccountPanel_support', icon: 'headset' },
  { id: 'setting', labelKey: 'AccountPanel_setting', icon: 'gear' },
  { id: 'language', labelKey: 'AccountPanel_language', icon: 'globe' },
]

const showLanguageModal = ref(false)
const currentLang = ref(localStorage.getItem('user_language') || 'en')

const handleMenuClick = (item: { id: string; labelKey: string; icon: string }) => {
  if (item.id === 'language') {
    showLanguageModal.value = true
  } else {
    emit('navigate', item.id)
  }
}

const selectLanguage = (code: string) => {
  currentLang.value = code
  hall.setLanguage(code)
  showLanguageModal.value = false
}
</script>

<template>
  <div class="account-panel">
    <!-- PC Two-Column Layout -->
    <div class="account-body">

      <!-- LEFT COLUMN: Profile + Actions -->
      <div class="col-left">
        <!-- Profile Card -->
        <div class="profile-card" @click="emit('navigate', 'ProfileDetail')">
          <div class="profile-glow"></div>
          <div class="avatar-wrap">
            <div class="avatar-ring">
              <img src="@/assets/hall/usericons/1.webp" class="avatar-img" />
            </div>
            <div class="camera-badge">
              <svg viewBox="0 0 24 24" width="11" height="11"><path fill="white" d="M12 18q-2.075 0-3.537-1.463Q7 15.075 7 13q0-2.075 1.463-3.537Q9.925 8 12 8q2.075 0 3.537 1.463Q17 10.925 17 13q0 2.075-1.463 3.537Q14.075 18 12 18Zm0-2q1.25 0 2.125-.875T15 13q0-1.25-.875-2.125T12 10q-1.25 0-2.125.875T9 13q0 1.25.875 2.125T12 16Zm-6 6q-.825 0-1.412-.587Q4 20.825 4 20V7q0-.825.588-1.413Q5.175 5 6 5h3l1.5-2h3L15 5h3q.825 0 1.413.587Q20 6.175 20 7v13q0 .825-.587 1.413Q18.825 22 18 22Z"/></svg>
            </div>
          </div>

          <div class="profile-info">
            <div class="name-row">
              <span class="user-name">{{ userName }}</span>
              <svg viewBox="0 0 24 24" width="13" height="13" class="edit-icon"><path fill="#888" d="M5 19h1.4l9.625-9.625l-1.4-1.4L5 17.6V19ZM3 21v-4.25L16.2 3.575q.3-.275.663-.425t.762-.15q.4 0 .775.15t.65.45L20.425 5q.3.275.438.65t.137.775q0 .4-.15.763t-.425.662L7.25 21H3Z"/></svg>
            </div>
            <div class="vip-badge">
              <img src="@/assets/hall/vip/vip_icon01_lv1.webp" class="vip-icon-mini" />
              <span class="vip-text">VIP0</span>
            </div>
            <div class="id-row">
              <span class="uid">ID: {{ userId }}</span>
              <svg viewBox="0 0 24 24" width="12" height="12" class="copy-icon"><path fill="var(--theme-primary)" d="M7 18q-.825 0-1.412-.587Q5 16.825 5 16V4q0-.825.588-1.413Q6.175 2 7 2h10q.825 0 1.413.587Q19 3.175 19 4v12q0 .825-.587 1.413Q17.825 18 17 18Zm0-2h10V4H7v12Zm-4 6q-.825 0-1.413-.587Q1 20.825 1 20V7q0-.425.288-.712Q1.575 6 2 6t.713.288Q3 6.575 3 7v13h10q.425 0 .713.288t.287.712q0 .425-.287.713t-.713.287Z"/></svg>
            </div>
          </div>

          <div class="profile-chevron">
            <svg viewBox="0 0 24 24" width="22" height="22"><path fill="#555" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/></svg>
          </div>
        </div>

        <!-- Stats Row -->
        <div class="stats-row">
          <div class="stat-card">
            <span class="stat-val">Rs 0.00</span>
            <span class="stat-lbl">Total Deposit</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-card">
            <span class="stat-val">Rs 0.00</span>
            <span class="stat-lbl">Total Withdraw</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-card">
            <span class="stat-val">0</span>
            <span class="stat-lbl">Referral</span>
          </div>
        </div>

        <!-- Deposit / Withdraw -->
        <div class="action-grid">
          <button class="action-btn deposit-btn" @click="emit('navigate', 'Deposit')">
            <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/></svg>
            {{ hall.Language.AccountPanel_deposit }}
          </button>
          <button class="action-btn withdraw-btn" @click="emit('navigate', 'Withdraw')">
            <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15c0-1.09 1.01-1.85 2.7-1.85c1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61c0 2.31 1.91 3.46 4.7 4.13c2.5.6 3 1.48 3 2.41c0 .69-.49 1.79-2.7 1.79c-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55c0-2.84-2.43-3.81-4.7-4.4z"/></svg>
            {{ hall.Language.AccountPanel_withdraw }}
          </button>
        </div>
      </div>

      <!-- RIGHT COLUMN: Menu Groups -->
      <div class="col-right">
        <div class="menu-card">
          <div class="menu-card-title">Account</div>
          <div v-for="item in menuGroup1" :key="item.id" class="menu-row" @click="handleMenuClick(item)">
            <div class="menu-row-left">
              <div class="icon-pill">
                <svg v-if="item.icon === 'wallet'" viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M20 18H4V6h16v12zm-2-10H6v8h12V8zM8 10h8v4H8v-4z"/></svg>
                <svg v-if="item.icon === 'diamond'" viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
                <svg v-if="item.icon === 'document'" viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
                <svg v-if="item.icon === 'refresh'" viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6c0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0 0 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6c0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 0 0 4 12c0 4.42 3.58 8 8 8v3l4-4l-4-4v3z"/></svg>
              </div>
              <span class="menu-label">{{ (hall.Language as Record<string, string>)[item.labelKey] }}</span>
            </div>
            <svg viewBox="0 0 24 24" width="18" height="18" class="row-chevron"><path fill="#555" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/></svg>
          </div>
        </div>

        <div class="menu-card">
          <div class="menu-card-title">More</div>
          <div v-for="item in menuGroup2" :key="item.id" class="menu-row" @click="handleMenuClick(item)">
            <div class="menu-row-left">
              <div class="icon-pill">
                <svg v-if="item.icon === 'mail'" viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5l-8-5V6l8 5l8-5v2z"/></svg>
                <svg v-if="item.icon === 'headset'" viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M12 2c-4.97 0-9 4.03-9 9v7c0 1.1.9 2 2 2h3v-8H5v-1c0-3.87 3.13-7 7-7s7 3.13 7 7v1h-3v8h3c1.1 0 2-.9 2-2v-7c0-4.97-4.03-9-9-9z"/></svg>
                <svg v-if="item.icon === 'gear'" viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M19.14 12.94c.04-.3.06-.61.06-.94c0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.488.488 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94c0 .31.04.64.09.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.49.49 0 0 0-.12-.61l-2.03-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6s3.6 1.62 3.6 3.6s-1.62 3.6-3.6 3.6z"/></svg>
                <svg v-if="item.icon === 'globe'" viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93c0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41c0 2.08-.8 3.97-2.1 5.39z"/></svg>
              </div>
              <span class="menu-label">{{ (hall.Language as Record<string, string>)[item.labelKey] }}</span>
            </div>
            <div class="menu-row-right">
              <span v-if="item.id === 'language'" class="current-val">{{ currentLang === 'en' ? 'English' : 'Urdu' }}</span>
              <svg viewBox="0 0 24 24" width="18" height="18" class="row-chevron"><path fill="#555" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/></svg>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Language Modal -->
    <Transition name="fade">
      <div v-if="showLanguageModal" class="modal-overlay" @click="showLanguageModal = false">
        <div class="modal-box" @click.stop>
          <div class="modal-header">
            <span class="modal-title">{{ hall.Language.Language_select_title }}</span>
            <button class="close-btn" @click="showLanguageModal = false">
              <svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
            </button>
          </div>
          <div class="lang-list">
            <div class="lang-item" :class="{ active: currentLang === 'en' }" @click="selectLanguage('en')">
              <div class="lang-flag">🇬🇧</div>
              <span class="lang-name">{{ hall.Language.Language_en }}</span>
              <svg v-if="currentLang === 'en'" viewBox="0 0 24 24" width="20" height="20"><path fill="var(--theme-primary)" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
            </div>
            <div class="lang-item" :class="{ active: currentLang === 'pak' }" @click="selectLanguage('pak')">
              <div class="lang-flag">🇵🇰</div>
              <span class="lang-name">{{ hall.Language.Language_pak }}</span>
              <svg v-if="currentLang === 'pak'" viewBox="0 0 24 24" width="20" height="20"><path fill="var(--theme-primary)" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* ===========================
   Base
   =========================== */
.account-panel {
  width: 100%;
  min-height: calc(100vh - 56px);
  padding: 16px;
  box-sizing: border-box;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #fff;
}

/* ===========================
   Body Layout (single col mobile, two col PC)
   =========================== */
.account-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 1100px;
  margin: 0 auto;
}

@media (min-width: 768px) {
  .account-body {
    flex-direction: row;
    align-items: flex-start;
  }

  .col-left {
    flex: 0 0 340px;
    position: sticky;
    top: 72px;
  }

  .col-right {
    flex: 1;
    min-width: 0;
  }
}

.col-left,
.col-right {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ===========================
   Profile Card
   =========================== */
.profile-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 20px;
  padding: 24px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(6px);
  transition: background 0.2s, border-color 0.2s;
}

.profile-card:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.14);
}

.profile-glow {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 130px;
  height: 130px;
  background: var(--theme-primary);
  border-radius: 50%;
  opacity: 0.07;
  pointer-events: none;
}

.avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.avatar-ring {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  padding: 2px;
  background: linear-gradient(135deg, var(--theme-primary), var(--theme-accent));
  box-shadow: 0 0 16px rgba(76, 163, 46, 0.35);
}

.avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.camera-badge {
  position: absolute;
  bottom: 1px;
  right: 1px;
  background: rgba(20, 20, 20, 0.9);
  border: 1px solid #444;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.profile-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.name-row {
  display: flex;
  align-items: center;
  gap: 7px;
}

.user-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
}

.edit-icon {
  opacity: 0.5;
}

.vip-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(to right, #3a3a3a, #252525);
  border: 1px solid #c5924a;
  border-radius: 99px;
  padding: 2px 9px 2px 3px;
  width: fit-content;
}

.vip-icon-mini {
  width: 16px;
  height: 16px;
}

.vip-text {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--theme-accent);
}

.id-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.uid {
  font-size: 0.82rem;
  color: #888;
}

.copy-icon {
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
}
.copy-icon:hover {
  opacity: 1;
}

.profile-chevron {
  display: flex;
  align-items: center;
}

/* ===========================
   Stats Row
   =========================== */
.stats-row {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  backdrop-filter: blur(6px);
}

.stat-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-val {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--theme-accent, #ffda6b);
}

.stat-lbl {
  font-size: 0.7rem;
  color: #888;
  white-space: nowrap;
}

.stat-divider {
  width: 1px;
  height: 32px;
  background: rgba(255, 255, 255, 0.1);
}

/* ===========================
   Action Buttons
   =========================== */
.action-grid {
  display: flex;
  gap: 12px;
}

.action-btn {
  flex: 1;
  height: 48px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.15s, box-shadow 0.15s;
}

.action-btn:hover {
  transform: translateY(-2px);
}

.action-btn:active {
  transform: scale(0.97);
}

.deposit-btn {
  background: var(--theme-primary);
  color: #fff;
  box-shadow: 0 4px 14px rgba(76, 163, 46, 0.35);
}

.withdraw-btn {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #ccc;
}

/* ===========================
   Menu Cards
   =========================== */
.menu-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  overflow: hidden;
  backdrop-filter: blur(6px);
}

.menu-card-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #666;
  padding: 14px 18px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.menu-row {
  height: 54px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: background 0.15s;
}

.menu-row:last-child {
  border-bottom: none;
}

.menu-row:hover {
  background: rgba(255, 255, 255, 0.05);
}

.menu-row:active {
  background: rgba(255, 255, 255, 0.08);
}

.menu-row-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.icon-pill {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: center;
  align-items: center;
  color: #aaa;
  flex-shrink: 0;
}

.menu-label {
  font-size: 0.95rem;
  color: #ddd;
  font-weight: 500;
}

.menu-row-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.current-val {
  font-size: 0.85rem;
  color: #888;
}

.row-chevron {
  opacity: 0.45;
}

/* ===========================
   Language Modal
   =========================== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 20000;
}

@media (min-width: 768px) {
  .modal-overlay {
    align-items: center;
    left: 80px;
  }

  .modal-box {
    border-radius: 20px !important;
    max-width: 420px;
    animation: scale-in 0.2s ease-out !important;
  }
}

.modal-box {
  width: 100%;
  background: #1c1c1e;
  border-radius: 24px 24px 0 0;
  padding: 24px 20px 32px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  animation: slide-up 0.25s ease-out;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
}

.close-btn {
  background: rgba(255, 255, 255, 0.08);
  border: none;
  color: #aaa;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
}

.lang-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.lang-item {
  height: 54px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  color: #ddd;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.lang-item:hover {
  background: rgba(255, 255, 255, 0.07);
}

.lang-item.active {
  border-color: var(--theme-primary);
  background: rgba(76, 163, 46, 0.1);
  color: #fff;
}

.lang-flag {
  font-size: 1.4rem;
  line-height: 1;
}

.lang-name {
  flex: 1;
  font-size: 0.95rem;
  font-weight: 500;
}

/* ===========================
   Animations
   =========================== */
@keyframes slide-up {
  from { transform: translateY(60px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}

@keyframes scale-in {
  from { transform: scale(0.92); opacity: 0; }
  to   { transform: scale(1);    opacity: 1; }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
